import React, { useRef, useEffect, useState } from "react";
import { getForgeToken } from "../../helpers/actions/RequestData";
import { useDispatch } from "react-redux";
import {
  AUTH_TIMER,
  updateGlobalState,
} from "../../helpers/reducers/GlobalReducer";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";

/* global Autodesk, THREE */ // eslint-disable-line

const ReusableViewer = ({
  urns,
  onViewerReady,
  onViewerError,
  viewerId = `reusableViewerDiv_${Date.now()}_${Math.random()}`,
  height = "500px",
  width = "100%",
  markupState,
}) => {
  const viewerRef = useRef(null);
  const viewerAppRef = useRef(null);
  const dispatch = useDispatch();
  const isInitialized = useRef(false);
  const isInitializing = useRef(false); // Add this to prevent concurrent initializations
  const [loadingModal, setLoadingModal] = useState(false);
  const [numberOfLoadedModels, setNumberOfLoadedModels] = useState(0);
  const [totalModelsToLoad, setTotalModelsToLoad] = useState(0);

  let viewerApp;
  let modelArrayData;
  let viewer;
  let showRedDbIds = [];
  let showRed = true;

  useEffect(() => {
    // Only initialize if we have URNs, haven't initialized, and aren't currently initializing
    if (
      urns &&
      urns.length > 0 &&
      !isInitialized.current &&
      !isInitializing.current
    ) {
      isInitializing.current = true; // Set flag to prevent concurrent calls

      const checkElement = () => {
        const element = document.getElementById(viewerId);
        if (element && element.offsetParent !== null) {
          // isInitialized.current = true;
          // initializeViewer();
          newInitializeViewer();
        } else {
          setTimeout(checkElement, 200);
        }
      };

      setTimeout(checkElement, 500);
    }

    return () => {
      // Cleanup viewer on unmount
      if (viewerAppRef.current) {
        try {
          viewerAppRef.current.finish();
          viewerAppRef.current = null;
          isInitialized.current = false;
          isInitializing.current = false;
        } catch (error) {
          console.error("Error cleaning up viewer:", error);
        }
      }
    };
  }, [viewerId]); // Remove urns from dependency array to prevent re-initialization

  const updateTimer = (expiry) => {
    dispatch(
      updateGlobalState({
        value: expiry,
        stateName: AUTH_TIMER,
      }),
    );
  };

  function loadManifest(documentId) {
    return new Promise((resolve, reject) => {
      const onDocumentLoadSuccess = (doc) => {
        onDocumentLoadSuccessActions(doc);
        doc.downloadAecModelData(() => resolve(doc));
      };
      Autodesk.Viewing.Document.load(documentId, onDocumentLoadSuccess, reject);
    });
  }

  const onDocumentLoadSuccessActions = async (doc) => {
    let viewables = doc.getRoot().getDefaultGeometry();
    viewerApp.viewer.setGroundShadow(false);
  };

  const onLoadModelSuccess = async (viewer, item) => {
    viewerApp.viewer.getToolbar();
    viewerApp.viewer.setTheme("light-theme");
    viewerApp.viewer
      .loadExtension("Autodesk.Measure")
      .then(function (measureExtension) {
        measureExtension.setUnits("mm");
      });
  };

  const onObjectTreeCreated = async (model) => {
    const instanceTree = model.getData().instanceTree;
    const allDbIdsStr = Object.keys(instanceTree.nodeAccess.dbIdToIndex);
    const allDbIdsInt = allDbIdsStr.map(function (id) {
      return parseInt(id);
    });
    return new Promise((resolve, reject) => {
      model.getBulkProperties2(
        allDbIdsInt,
        { propFilter: ["MUID", "name", "externalId"] },
        //propFilter: ["_RC","MUID", "Tag Number"]
        (data) => {
          resolve({ data, model: model });
        },
        reject,
      );
    });
  };

  //New Model Launcher
  const newInitializeViewer = () => {
    const options = {
      env: "AutodeskProduction",
      getAccessToken: (token) => {
        getForgeToken(token, updateTimer);
      },
    };

    const options3d = {
      viewerConfig: {
        disableBimWalkInfoIcon: true,
        extensions: [
          "Autodesk.Viewing.MarkupsCore",
          "Autodesk.PDF",
          "Autodesk.Measure",
          "Autodesk.AEC.Minimap3DExtension",
          // "Autodesk.Snapping",
          // "LevelToolExtension",
          // "Autodesk.DocumentBrowser",
          // "Autodesk.Geolocation",
          // "GisToolExtension",
        ],
        disabledExtensions: {
          layermanage: true,
          explode: true,
          bimwalk: true,
          fusionOrbit: true,
          propertiesPanel: true,
        },
      },
    };

    Autodesk.Viewing.Initializer(options, function () {
      //get the viewer div
      viewer = document.getElementById(viewerId);
      //initialize the viewer object
      viewerApp = new Autodesk.Viewing.AggregatedView();
      viewerApp.init(viewer, options3d);
      const tasks = [];
      urns.forEach((urn) => tasks.push(loadManifest("urn:" + urn)));
      setTotalModelsToLoad(urns.length); // <-- Set total models to load

      Promise.all(tasks)
        .catch((error) => {
          console.error("Error loading manifests:", error);
          if (onViewerError) onViewerError(error);
        })
        .then((docs) =>
          Promise.resolve(
            docs.map((doc) => {
              const bubbles = doc.getRoot().search({ type: "geometry" });

              let bubble;
              bubble = bubbles[0];
              if (!bubble) return null;
              const modelObject = {
                bubble: bubble,
                urn: doc.myData.guid,
              };
              return modelObject;
            }),
          ),
        )
        .then(async (modelArray) => {
          modelArrayData = modelArray;
          const bubbles = modelArray.map((model) => model.bubble);
          const loadModel = async () => {
            viewerApp.setNodes(bubbleArray);
            await viewerApp.waitForLoadDone({
              propDb: true,
              geometry: true,
            });
            return;
          };

          const bubbleArray = [];
          setLoadingModal(true);

          for (const bubble of bubbles) {
            bubbleArray.push(bubble);
            setNumberOfLoadedModels(bubbleArray.length); // <-- Update loaded count
            if (
              !(bubbleArray.length % 5) ||
              bubbleArray.length === bubbles.length
            ) {
              await loadModel();
            }
          }

          setLoadingModal(false);
          //If MarkupState then use the markup extension to set the state

          onLoadModelSuccess();
          if (markupState) {
            const markupExtension = await viewerApp.viewer.loadExtension(
              "Autodesk.Viewing.MarkupsCore",
            );

            markupExtension.viewer.restoreState(
              JSON.parse(JSON.stringify(markupState)),
            );
          }

          var ext = viewerApp.viewer.getExtension("Autodesk.PropertiesManager");
          ext?._toolbarButton?.removeFromParent();

          if (!viewerApp.viewer || !viewerApp.viewer.model) {
            console.warn("Viewer or model not ready");
            return;
          }
        });
    });
  };

  return (
    <>
      <div
        ref={viewerRef}
        id={viewerId}
        // style={{
        //   height: height,
        //   width: width,
        //   border: "1px solid #ccc",
        //   position: "relative",
        //   minHeight: "600px",
        //   minWidth: "400px",
        // }}
      />
      <Modal show={loadingModal} dialogClassName="loading-modal" centered>
        <Modal.Body className="loading-modal__body">
          <div className="row">
            <div className="col">
              <div className="text-center loading-modal__content">
                <h4 className="loading-modal__title">
                  Loading 3D Model...
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="loading-modal__spinner"
                  />
                </h4>
                <p className="loading-modal__copy">
                  Please wait while the model is being loaded. This may take a
                  few moments.
                </p>
                {(() => {
                  const safeTotal = totalModelsToLoad || 0;
                  const safeLoaded = Math.min(numberOfLoadedModels, safeTotal);
                  const progressPct =
                    safeTotal > 0 ? (safeLoaded / safeTotal) * 100 : 0;

                  return (
                    <>
                      <p className="loading-modal__counter">{`${safeLoaded} of ${safeTotal} loaded.`}</p>
                      <div className="progress loading-modal__progress">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated loading-modal__progress-bar"
                          role="progressbar"
                          aria-valuenow={safeLoaded}
                          aria-valuemin="0"
                          aria-valuemax={safeTotal}
                          style={{
                            width: `${progressPct}%`,
                          }}
                        ></div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ReusableViewer;
